import {
  Box,
  Button,
  Checkbox,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import React, { useCallback, useMemo, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { HiMagnifyingGlass } from "react-icons/hi2";
import { showErrorToast, showSuccessToast } from "../../../utils";
import { useLinkToParent, useUnlinkFromParent } from "../../../hooks/query/dimensions/dimensionAdmin";
import { useGetDimensions } from "../../../hooks/query/dimensions/dimensions";

function LinkToParentModal({ isOpen, onClose, item, dimensions, onSuccess }) {
  const toast = useToast();
  const existingParentIds = new Set(item.parentIds || []);
  const [selectedIds, setSelectedIds] = useState(new Set(existingParentIds));
  const [searchQuery, setSearchQuery] = useState("");
  const [scrollNode, setScrollNode] = useState(null);
  const scrollRef = useCallback((node) => setScrollNode(node), []);

  // Find the parent dimension level (one above this item's level)
  const currentLevelIndex = dimensions.findIndex((d) => d.level === item.dimensionLevel);
  const parentDimension = currentLevelIndex > 0 ? dimensions[currentLevelIndex - 1] : null;

  // Fetch parent dimension items
  const { data: dimensionsData } = useGetDimensions({}, { enabled: isOpen && !!parentDimension });

  const levelKey = parentDimension
    ? `dimension${["One", "Two", "Three", "Four", "Five"][dimensions.indexOf(parentDimension)]}`
    : null;
  const parentItems = useMemo(() => {
    if (!dimensionsData || !levelKey) return [];
    return dimensionsData[levelKey]?.items || [];
  }, [dimensionsData, levelKey]);

  const filteredParentItems = useMemo(() => {
    if (!searchQuery.trim()) return parentItems;
    const q = searchQuery.toLowerCase();
    return parentItems.filter((i) => i.name.toLowerCase().includes(q));
  }, [parentItems, searchQuery]);

  const virtualizer = useVirtualizer({
    count: filteredParentItems.length,
    getScrollElement: () => scrollNode,
    estimateSize: () => 42,
  });

  const { mutate: linkToParent } = useLinkToParent({
    onError: (err) => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
  });

  const { mutate: unlinkFromParent } = useUnlinkFromParent({
    onError: (err) => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
  });

  const toggleParent = (parentId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) {
        next.delete(parentId);
      } else {
        next.add(parentId);
      }
      return next;
    });
  };

  const toLink = [...selectedIds].filter((id) => !existingParentIds.has(id));
  const toUnlink = [...existingParentIds].filter((id) => !selectedIds.has(id));
  const hasChanges = toLink.length > 0 || toUnlink.length > 0;

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!hasChanges) {
      onClose();
      return;
    }
    setIsSaving(true);
    try {
      for (const parentId of toLink) {
        await new Promise((resolve, reject) => {
          linkToParent({ parentId, childId: item.id }, { onSuccess: resolve, onError: reject });
        });
      }
      for (const parentId of toUnlink) {
        await new Promise((resolve, reject) => {
          unlinkFromParent({ parentId, childId: item.id }, { onSuccess: resolve, onError: reject });
        });
      }
      const parts = [];
      if (toLink.length > 0) parts.push(`linked to ${toLink.length}`);
      if (toUnlink.length > 0) parts.push(`unlinked from ${toUnlink.length}`);
      showSuccessToast(toast, `"${item.name}" ${parts.join(", ")} parent(s).`);
      onSuccess();
      onClose();
    } catch (err) {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Manage parents for "{item.name}"</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={3}>
            {parentDimension ? (
              <>
                <Text fontSize="sm" color="gray.600">
                  Select {parentDimension.name} items to link this {item.dimensionName} item under:
                </Text>
                <Text fontSize="xs" color="orange.600" fontStyle="italic">
                  Note: Linking only changes the hierarchy — it does not move existing data. Use Merge to transfer data between items.
                </Text>
                {parentItems.length === 0 ? (
                  <Text fontSize="sm" color="gray.400">
                    No parent items available.
                  </Text>
                ) : (
                  <Box>
                    <InputGroup size="sm" mb={2}>
                      <InputLeftElement pointerEvents="none">
                        <HiMagnifyingGlass color="gray" />
                      </InputLeftElement>
                      <Input
                        placeholder={`Search ${parentItems.length} items...`}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </InputGroup>
                    {filteredParentItems.length === 0 ? (
                      <Text fontSize="sm" color="gray.400" py={2}>
                        No items match your search.
                      </Text>
                    ) : (
                      <Box ref={scrollRef} maxH="300px" overflowY="auto">
                        <Box style={{ height: `${virtualizer.getTotalSize()}px`, width: "100%", position: "relative" }}>
                          <Box style={{ position: "absolute", top: 0, left: 0, width: "100%", transform: `translateY(${virtualizer.getVirtualItems()[0]?.start ?? 0}px)` }}>
                            {virtualizer.getVirtualItems().map((virtualRow) => {
                              const parentItem = filteredParentItems[virtualRow.index];
                              const isChecked = selectedIds.has(parentItem.id);
                              return (
                                <Box
                                  key={parentItem.id}
                                  data-index={virtualRow.index}
                                  ref={virtualizer.measureElement}
                                  p={2}
                                  borderRadius="md"
                                  border="1px solid"
                                  borderColor={isChecked ? "limeShade.400" : "gray.200"}
                                  cursor="pointer"
                                  _hover={{ bg: "gray.50" }}
                                  onClick={() => toggleParent(parentItem.id)}
                                >
                                  <Checkbox isChecked={isChecked} pointerEvents="none" size="sm">
                                    <Text fontSize="sm" ml={1}>
                                      {parentItem.name}
                                    </Text>
                                  </Checkbox>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </>
            ) : (
              <Text fontSize="sm" color="gray.500">
                This item is at the top dimension level and cannot be linked to a parent.
              </Text>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} isLoading={isSaving} isDisabled={!hasChanges}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default LinkToParentModal;
